import { EventModel, EventStore } from "@bryntum/schedulerpro";
import { PlanEvent } from "../models/Event";
import { SwivuStoreArchiveMixin } from "./mixins/SwivuStoreArchiveMixin";

export class SwivuGeneratedPlanEventStore extends SwivuStoreArchiveMixin(EventStore) {
  static get configurable() {
    return {
      storeId: "swivu_fake_event_store",
      modelClass: PlanEvent
    };
  }

  get mainProject() {
    return this.project.mainProject;
  }

  #isCommitting = false

  bindPlanningDateStore(store) {
    this.detachListeners("planningDateStore");
    store.on({
      name: 'planningDateStore',
      change: 'onPlanningDateStoreChange',
      thisObj: this
    });
  }

  bindEventStore(store) {
    this.detachListeners('taskEventStore');
    this.detachListeners('projectListener');

    store.on({
      name: 'taskEventStore',
      change: 'onEventStoreChange',
      thisObj: this
    });
    this.project.mainProject.on({
      name: "projectListener",
      swivucommitstart: "onCommitStart",
      swivucommitend: "onCommitEnd",
      thisObj: this
    })
    this.resourcesToUpdate = new Set();
  }

  bindAssignmentStore(store) {
    this.detachListeners('taskResourceAssignmentStore');

    this.taskResourceAssignmentStore = store;

    store.on({
      name: 'taskResourceAssignmentStore',
      change: 'onTaskResourceAssignmentStoreChange',
      thisObj: this
    });
  }

  //#region Listeners

  onCommitStart() {
    this.#isCommitting = true;
  }

  onCommitEnd() {
    this.#isCommitting = false;
    this.resourcesToUpdate.forEach(r => this.updateTimeRangesForResource(r))
    this.resourcesToUpdate.clear();
  }

  onPlanningDateStoreChange(e) {
    const { action, record, records, changes  } = e;
    switch (action) {
      case 'update': {
        if (changes.id && changes.id.oldValue) {
          const phantomId = changes.id.oldValue;
          const planEventRecordWithPhantomId = this.project.eventStore.find((event) => event.planningDateId === phantomId);
          if (planEventRecordWithPhantomId) {
            planEventRecordWithPhantomId.planningDateId = record.id;
          }
        }
        const planningDateRecordToUpdate = this.project.eventStore.find((planningDateRecord) => planningDateRecord.planningDateId === record.id);
        if (planningDateRecordToUpdate) {
          planningDateRecordToUpdate.name = record.name;
          planningDateRecordToUpdate.startDate = record.startDate;
          planningDateRecordToUpdate.endDate = record.endDate;
          planningDateRecordToUpdate.eventColor = record.color;
        }
        break;
      }
      case 'add': {
        // Todo: fix adding a new record causes the phantomId being set (in planningDateId) of PlanningDate record in event record
        const planningDateRecordToAdd = records[0];
        const { planningDateEvent,  planningDateAssignment } = this.getNewTaskPlanningDateEventRecord(planningDateRecordToAdd);
        this.project.eventStore.add(planningDateEvent);
        this.project.assignmentStore.add(planningDateAssignment);
        break;
      }
      case 'remove': {
        const planningDateRecordToRemove = records[0];
        const planningDateEventRecordToRemove = this.project.eventStore.find((eventRecord) => eventRecord.planningDateId === planningDateRecordToRemove.id);
        const planningDateAssignmentRecordToRemove = this.project.assignmentStore.find((assignmentRecord) => assignmentRecord.eventId === planningDateEventRecordToRemove.id)

        this.project.eventStore.remove(planningDateEventRecordToRemove);
        this.project.assignmentStore.remove(planningDateAssignmentRecordToRemove);
        break;
      }
      default:
        break;
    }
  }

  onEventStoreChange({ action, record, records, changes }) {
    let tasksToUpdate = new Set();

    switch (action) {
      case 'update':
        if ('startDate' in changes || 'endDate' in changes || 'id' in changes) {
          tasksToUpdate.add(record.taskId);
        }
        break;
      case 'add':
      case 'remove':
        records.forEach(r => tasksToUpdate.add(r.taskId));
        break;
      default:
        break;
    }
    if (this.#isCommitting) {
      this.resourcesToUpdate.add(...tasksToUpdate)
    } else {
      tasksToUpdate.forEach(r => this.updateTimeRangesForResource(r))
    }
  }

  onTaskResourceAssignmentStoreChange({ record, action, changes }) {
    if (action === 'update' && 'resourceId' in changes) {
      if (this.#isCommitting) {
        this.resourcesToUpdate.add(record.event.taskId)
      } else {
        this.updateTimeRangesForResource(record.event.taskId);
      }
    }
  }

  getNewTaskPlanningDateEventRecord(taskPlanningDateRecord) {
    const taskId = taskPlanningDateRecord.taskId;
    const generatedEventId = EventModel.generateId();

    const planningDateEvent = {
      id: generatedEventId,
      name: taskPlanningDateRecord.name,
      startDate: taskPlanningDateRecord.startDate,
      endDate: taskPlanningDateRecord.endDate,
      taskId: taskId,
      // TODO: Need to look back here!
      // In Bryntum 7 multi-assignment mode, resource relationship is handled via AssignmentStore only
      // resource: { id: taskId }, // REMOVED for Bryntum 7 compatibility
      eventStyle: "dashed",
      eventColor: taskPlanningDateRecord.color,
      isPlanningDateEvent: true,
      planningDateId: taskPlanningDateRecord.id
    };

    const planningDateAssignment = {
      resource: taskId,
      event: generatedEventId,
    };

    return { planningDateEvent, planningDateAssignment };
  }

  //#endregion

  getPlanningDatesAsEventAndAssignments() {
    const taskPlanningDatesStore = this.mainProject.taskPlanningDatesStore;

    return taskPlanningDatesStore.reduce(({ planningDateEvents, planningDateAssignments }, taskPlanningDateRecord) => {
      const { planningDateEvent,  planningDateAssignment } = this.getNewTaskPlanningDateEventRecord(taskPlanningDateRecord);

      planningDateEvents.push(planningDateEvent);
      planningDateAssignments.push(planningDateAssignment);

      return { planningDateEvents, planningDateAssignments };

    }, { planningDateEvents: [], planningDateAssignments: [] });
  }

  populatePlanEventStore() {
    // Generate starting ranges and group them
    const ranges = this.generateRangesFromTaskEvents(this.mainProject.eventStore.getRange());

    // now iterate ranges again and generate list of non-overlapping/intersecting sub ranges
    const [events, assignments] = Object.values(ranges).reduce((acc, items) => {
      const [events, assignments] = this.mergeTimeRanges(items);

      acc[0].push(...events);
      acc[1].push(...assignments);

      return acc;
    }, [[], []]);

    const { planningDateEvents, planningDateAssignments } = this.getPlanningDatesAsEventAndAssignments();
    this.data = [...events, ...planningDateEvents];
    this.assignmentStore.data = [...planningDateAssignments, ...assignments];
  }

  mergeTimeRanges(ranges) {
    let initialValue = [[], [], null];

    if (!ranges || ranges.length === 0) {
      return initialValue;
    }

    const taskId = ranges[0].taskId;

    // go over ranges once and put all dates to the map
    const map = ranges.reduce((result, range) => {
        ['startDate', 'endDate'].forEach(key => {
          const value = getTime(range[key]);

          if (!(value in result)) {
              result[value] = [];
          }
        });

        return result;
    }, {});

    const sortedKeys = Object.keys(map).map(k => parseInt(k)).sort();

    // now go over ranges once again, find start index, end index and append name of
    // the range to every key in between
    ranges.forEach(range => {
        const start = getTime(range.startDate);
        const end   = getTime(range.endDate);
        const { resourceId } = range;

        const startIndex = sortedKeys.indexOf(start);
        const endIndex = sortedKeys.indexOf(end);

        for (let i = startIndex; i < endIndex; i++) {
            map[sortedKeys[i]].push(resourceId);
        }
    });

    // No we go over the array once again and collect the result
    const result = sortedKeys.reduce((acc, key) => {
        let [events, assignments, prev] = acc;

        if (!prev) {
            prev = { id: this.modelClass.generateId(), startDate : new Date(key), _taskEventResources: map[key].slice() }
        }
        else {
            prev.endDate = new Date(key);
            events.push(prev);
            assignments.push({ event: prev.id, resource: taskId });

            if (map[key].length) {
                prev = { id: this.modelClass.generateId(), startDate : new Date(key), _taskEventResources: map[key].slice() }
            }
            else {
                prev = null;
            }
        }

        acc[2] = prev;

        return acc;
    }, initialValue);

    return result;
  }

  /**
   * Returns object where keys are resource ids and values are time ranges for that resource
   * @param {Model[]} records Array of task events
   * @returns {Object}
   */
  generateRangesFromTaskEvents(records) {
    return records.reduce((result, taskEvent) => {
      const taskId = taskEvent.taskId;

      if (taskId) {
        if (!(taskId in result)) {
          result[taskId] = [];
        }

        result[taskId].push(this.generateRangeFromTaskEvent(taskEvent))
      }

      return result;
    }, {});
  }

  /**
   * Converts Task Event record to a corresponding resource time range config object
   * @param {Model} record Task event
   * @returns {Object} Config object for the resource range
   */
  generateRangeFromTaskEvent(record) {
    return {
      // todo: trace issue: dragging resource in plan view timeline from resource grid breaks app, randomly happen though
      startDate: record.startDate,
      endDate: record.endDate,
      name: record.displayName,
      eventId: record.id,
      taskId: record.taskId,
      resourceId: record.resource?.id
    }
  }

  updateTimeRangesForResource(resourceId) {
    // Remove old ranges for the resource, generate new
    this.remove(this.query(r => r.resourceId === resourceId && !r.isPlanningDateEvent));

    const task = this.mainProject.extraTaskStore.getById(resourceId);

    if (task) {
      const ranges = this.generateRangesFromTaskEvents(task.events);

      const [events, assignments] = this.mergeTimeRanges(ranges[resourceId]);

      this.assignmentStore.add(assignments);
      this.add(events);
    }
    else {
      console.warn(`Cannot find Task with ID ${resourceId}`);
    }
  }
}

function getTime(dateOrString) {
  return (dateOrString instanceof Date ? dateOrString : new Date(dateOrString)).getTime()
}
